import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Check, ShieldAlert, X } from "lucide-react";
import { MBackBar, MCard, MEmpty } from "@/components/mobile/ui/primitives";
import {
  useApprovalRequest,
  useBreakGlass,
  useDecideApproval,
} from "@/hooks/useApprovals";
import { humanizeAction, tierLabel } from "@/services/approvalsApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ActionKind = "APPROVE" | "DENY" | "BREAK_GLASS" | null;

export default function MobileApprovalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useApprovalRequest(id);
  const decide = useDecideApproval();
  const breakGlass = useBreakGlass();

  const [action, setAction] = useState<ActionKind>(null);
  const [reason, setReason] = useState("");

  const close = () => {
    setAction(null);
    setReason("");
  };

  const submit = async () => {
    if (!id || !action) return;
    try {
      if (action === "BREAK_GLASS") {
        if (!reason.trim()) return;
        await breakGlass.mutateAsync({ id, reason: reason.trim() });
      } else {
        await decide.mutateAsync({
          id,
          decision: action,
          reason: reason.trim() || undefined,
        });
      }
      close();
    } catch {
      // toast already handled in hook
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 pb-6 pt-2">
        <MBackBar title="Approval" onBack={() => navigate(-1)} />
        <MCard className="animate-pulse">
          <div className="h-4 w-1/2 rounded bg-[color:var(--m-fill-2)]" />
          <div className="mt-3 h-3 w-3/4 rounded bg-[color:var(--m-fill-2)]" />
        </MCard>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="px-4 pb-6 pt-2">
        <MBackBar title="Approval" onBack={() => navigate(-1)} />
        <MEmpty
          title="Couldn't load this request"
          description={(error as Error)?.message ?? "It may have expired."}
          icon={<AlertTriangle className="h-4 w-4" aria-hidden />}
        />
      </div>
    );
  }

  const pending = data.status === "PENDING";
  const required = data.quorumRequired ?? (data.riskTier === "TWO_PERSON" ? 2 : 1);
  const have = data.quorumCount ?? 0;

  return (
    <div className="px-4 pb-8 pt-2">
      <MBackBar title="Approval" onBack={() => navigate(-1)} />

      <MCard className="mb-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="rounded-md bg-[color:var(--m-fill-2)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[color:var(--m-ink)]">
            {tierLabel(data.riskTier)}
          </span>
          <span
            className={cn(
              "rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
              pending
                ? "bg-[color:var(--m-warn-bg)] text-[color:var(--m-warn)]"
                : "bg-[color:var(--m-fill-2)] text-[color:var(--m-gray)]",
            )}
          >
            {data.status.replace("_", " ")}
          </span>
        </div>
        <h2 className="m-h3">{humanizeAction(data.actionType)}</h2>
        <p className="m-sub">
          Requires {required} approver{required === 1 ? "" : "s"} ·{" "}
          {have} of {required} so far
        </p>
        <div className="mt-3 flex gap-1">
          {Array.from({ length: required }).map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1.5 w-7 rounded-sm",
                i < have
                  ? "bg-[color:var(--m-ink)]"
                  : "bg-[color:var(--m-line-2)]",
              )}
            />
          ))}
        </div>
      </MCard>

      <MCard className="mb-3" flush>
        <DefList
          rows={[
            ["Privilege", <span className="m-mono text-[12px]">{data.privilegeName}</span>],
            ["Action", <span className="m-mono text-[12px]">{data.actionType}</span>],
            ["Requester", data.requesterEmail || data.requesterId || "—"],
            ["Created", data.createdAt ?? "—"],
            ["Expires", data.expiresAt ?? "—"],
            ["Tenant", <span className="m-mono text-[11.5px] text-[color:var(--m-gray)]">{data.tenantId}</span>],
          ]}
        />
      </MCard>

      {data.targets && data.targets.length > 0 && (
        <MCard className="mb-3">
          <p className="m-section-label mb-2">Targets ({data.targets.length})</p>
          <ul className="space-y-1 text-[12px] text-[color:var(--m-ink)]">
            {data.targets.slice(0, 20).map((t, i) => (
              <li key={i} className="m-mono truncate">
                {typeof t === "string" ? t : JSON.stringify(t)}
              </li>
            ))}
            {data.targets.length > 20 && (
              <li className="text-[11px] text-[color:var(--m-gray-3)]">
                …and {data.targets.length - 20} more
              </li>
            )}
          </ul>
        </MCard>
      )}

      {data.reason && (
        <MCard className="mb-3">
          <p className="m-section-label mb-1.5">Reason</p>
          <p className="text-[12.5px] leading-relaxed text-[color:var(--m-ink)]">
            {data.reason}
          </p>
        </MCard>
      )}

      {pending && (
        <div className="sticky bottom-[calc(env(safe-area-inset-bottom)+4rem)] z-10 mt-4 flex gap-2 rounded-2xl border border-[color:var(--m-line)] bg-white/95 p-2 shadow-lg backdrop-blur">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setAction("DENY")}
          >
            <X className="h-4 w-4" /> Deny
          </Button>
          <Button
            className="flex-1 bg-[color:var(--m-ink)] text-white hover:bg-[color:var(--m-ink)]/90"
            onClick={() => setAction("APPROVE")}
          >
            <Check className="h-4 w-4" /> Approve
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Break-glass"
            onClick={() => setAction("BREAK_GLASS")}
            title="Break-glass override"
          >
            <ShieldAlert className="h-4 w-4 text-[color:var(--m-crit)]" />
          </Button>
        </div>
      )}

      <Dialog open={!!action} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {action === "APPROVE" && "Approve request"}
              {action === "DENY" && "Deny request"}
              {action === "BREAK_GLASS" && "Break-glass override"}
            </DialogTitle>
            <DialogDescription>
              {action === "BREAK_GLASS"
                ? "Reason is required and recorded in a hardened audit event. This bypasses the approval quorum."
                : "Add an optional note. Your decision is recorded in the audit trail."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              action === "BREAK_GLASS"
                ? "Required: justify bypassing quorum…"
                : "Optional reason"
            }
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                (action === "BREAK_GLASS" && !reason.trim()) ||
                decide.isPending ||
                breakGlass.isPending
              }
              className={cn(
                action === "DENY" &&
                  "bg-[color:var(--m-crit)] text-white hover:bg-[color:var(--m-crit)]/90",
                action === "APPROVE" &&
                  "bg-[color:var(--m-ink)] text-white hover:bg-[color:var(--m-ink)]/90",
                action === "BREAK_GLASS" &&
                  "bg-[color:var(--m-crit)] text-white hover:bg-[color:var(--m-crit)]/90",
              )}
            >
              {action === "BREAK_GLASS" ? "Execute break-glass" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DefList({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="divide-y divide-[color:var(--m-line)]">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-start justify-between gap-4 px-4 py-2.5">
          <dt className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--m-gray-3)]">
            {k}
          </dt>
          <dd className="text-right text-[12.5px] text-[color:var(--m-ink)]">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
